import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useDispatch } from 'react-redux';
import { useCustomerStatus } from '@wearejh/m2-pwa-user/lib/hooks/useCustomerStatus';

import CHECK_SESSION_QUERY from './useCheckSessionQuery.graphql';

export const SESSION_CHECK_INTERVAL = 3 * 60 * 1000;

/**
 * Custom hook that manages automatic session validation for signed-in users.
 *
 * This hook periodically checks if the user's session is still valid by making
 * GraphQL queries to verify the customer's authentication status. It automatically
 * logs out users when their session expires or becomes invalid.
 *
 * Features:
 * - Runs session checks every 3 minutes for signed-in users
 * - Performs immediate checks when the browser tab becomes visible
 * - Automatically logs out users on session validation failures
 * - Includes rate limiting to prevent excessive API calls
 * - Cleans up intervals and event listeners on unmount
 *
 * @returns {null} This hook doesn't return any values - it manages session state internally
 *
 * @example
 * ```tsx
 * function App() {
 *   useSessionCheck();
 *
 *   return <div>Your app content</div>;
 * }
 * ```
 */
export const useSessionCheck = () => {
    const dispatch = useDispatch();
    const { isSignedIn } = useCustomerStatus();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastCheckRef = useRef<number>(Date.now());

    const handleSessionError = useCallback(() => {
        console.warn('Session check failed, logging out user');
        dispatch({ type: 'User.SignOut' });
    }, [dispatch]);

    const { refetch } = useQuery(CHECK_SESSION_QUERY, {
        skip: !isSignedIn,
        fetchPolicy: 'no-cache',
        context: {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0',
            },
        },
        onError: () => {
            handleSessionError();
        },
        onCompleted: (responseData) => {
            if (!responseData?.customer?.email) {
                handleSessionError();
            }
        },
    });

    const checkSession = useCallback(() => {
        if (!isSignedIn) {
            return;
        }

        lastCheckRef.current = Date.now();

        return refetch()
            .then((result) => {
                if (result.errors && result.errors.length > 0) {
                    console.warn('Session check refetch returned GraphQL errors:', result.errors);
                    handleSessionError();
                    return;
                }

                if (!result.data?.customer?.email) {
                    console.warn('Session check refetch returned no customer.email');
                    handleSessionError();
                    return;
                }
            })
            .catch((error) => {
                console.error('Session check error:', error);
                handleSessionError();
                return;
            });
    }, [handleSessionError, isSignedIn, refetch]);

    const resetInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (isSignedIn) {
            intervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
        }
    }, [isSignedIn, checkSession]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const timeSinceLastCheck = Date.now() - lastCheckRef.current;
                const minimumCheckInterval = 30000;

                if (isSignedIn && timeSinceLastCheck >= minimumCheckInterval) {
                    checkSession();
                }

                resetInterval();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        resetInterval();

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [checkSession, isSignedIn, resetInterval]);

    return null;
};
